import { Alerts } from "../../../utils/Alerts.js";
import { DateTime as DateTime$1 } from "../../../components/DateTime/index.js";
import { useCurrentUser } from "../../AuthWrapper2/hooks/useCurrentUser.js";
import { useRPCContext } from "../../../contexts/RPCContext.js";
import { UserName } from "../../Users/shared/UserName.js";
import { UserGeoRelativeLocationLabel } from "../../Users/shared/UserGeoRelativeLocationLabel.js";
import { UserAnPCheck } from "../UserAnPCheck.js";
import { useUrlState } from "../../../hooks/useUrlState.js";
import { useRPCServerEventHandler } from "../../../hooks/useRPCServerEventHandler/index.js";
import { useAppInteractionHandlers } from "../../AppWrapper2/hooks/useAppInteractionHandlers.js";
import { LocationNames } from "../../Locations/shared/LocationNames.js";
import { ACTIVITY_FEED_SCOPE_LABELS } from "./constants.js";
import { ActivityFeedSearchFilters } from "./ActivityFeedSearchFilters.js";
import { determineActivityOnClick } from "./determine-activity-on-click.js";
import { getActivityKey } from "./getActivityKey.js";
import { ActivityFeedService } from "./services/ActivityFeedService.js";
import { ActivityFeedViewModel, DEFAULT_FILTERS, createActivityFeedState } from "./viewmodels/ActivityFeedViewModel.js";
import { Tags } from "../../Tags/shared/Tags.js";
import dayjs from "dayjs";
import { useEffect, useMemo } from "react";
import { Icon, Loading } from "@therms/atalaya";
import { FaCircle, FaListUl } from "react-icons/fa";
import { jsx, jsxs } from "react/jsx-runtime";
import { getColorContrast } from "@therms/web-js";
import { useLocal } from "mvc-kit/react";
import { castArray } from "lodash-es";
import { MdGpsFixed } from "react-icons/md";
import { useAutoAnimate } from "@formkit/auto-animate/react";
import { InfiniteScroll } from "mvc-kit/react/ui";
//#region src/modules/shared/ActivityFeed/ActivityFeed.tsx
function ActivityFeed({ locationId, userId }) {
	const { rpcService } = useRPCContext();
	const appInteractionHandlers = useAppInteractionHandlers();
	const currentUser = useCurrentUser();
	const service = useMemo(() => new ActivityFeedService(rpcService), [rpcService]);
	const [, setUrlFilters] = useUrlState("actfeedfilters", DEFAULT_FILTERS);
	const vm = useLocal(ActivityFeedViewModel, createActivityFeedState(readInitialFilters()), {
		locationId,
		onFiltersChange: setUrlFilters,
		service,
		userId
	});
	const loadError = vm.async.loadInitial?.error;
	useEffect(() => {
		if (loadError) Alerts.error("Failed to load activity feed", loadError);
	}, [loadError]);
	useRPCServerEventHandler("OpsActivityFeedUpdated", (_event, { activity }) => vm.insertRealtimeItem(activity));
	const [autoAnimateParentRef] = useAutoAnimate({});
	return /* @__PURE__ */ jsxs("div", {
		className: "w-full",
		children: [/* @__PURE__ */ jsx(ActivityFeedSearchFilters, {
			filtersState: vm.state.filters,
			setFiltersState: (partial) => vm.setFilters(partial),
			showLocations: !locationId,
			showUsers: !userId
		}), /* @__PURE__ */ jsx(InfiniteScroll, {
			hasMore: !vm.state.noMoreSkips,
			loading: !!vm.async.loadMore?.loading || !!vm.async.loadInitial?.loading,
			onLoadMore: () => vm.loadMore(),
			renderEnd: () => /* @__PURE__ */ jsxs("div", {
				className: "pt-xxl text-neutral flex w-full flex-col items-center justify-center",
				children: [/* @__PURE__ */ jsx("span", { children: /* @__PURE__ */ jsx(Icon, {
					Icon: FaListUl,
					size: "sm"
				}) }), /* @__PURE__ */ jsx("span", { children: vm.state.items.length > 0 ? "No more activity to load" : "No activity to display" })]
			}),
			renderLoading: () => /* @__PURE__ */ jsx("div", {
				className: "p-base flex w-full items-center justify-center",
				children: /* @__PURE__ */ jsx(Loading, {})
			}),
			children: /* @__PURE__ */ jsx("div", {
				className: "gap-sm top-0 isolate w-full",
				children: vm.groupedByDate.map(([date, activities]) => /* @__PURE__ */ jsxs("div", { children: [/* @__PURE__ */ jsx("div", {
					className: "text-neutral text-md py-xs px-xs bg-surface sticky top-0 z-10 w-full font-light",
					children: dayjs(date).format("MMM Do ddd")
				}), activities.length > 0 && /* @__PURE__ */ jsx("div", {
					className: "ml-base py-xxs space-y-1 text-sm",
					ref: autoAnimateParentRef,
					children: activities.map((activity) => {
						const { activityTypeTags, description, detail, locationId, subTitle, timestamp, title, user } = activity;
						return /* @__PURE__ */ jsxs("div", {
							className: "gap-base hover:bg-surface-strong grid grid-cols-[100px_1fr] hover:cursor-pointer",
							onClick: () => determineActivityOnClick(appInteractionHandlers, detail),
							children: [/* @__PURE__ */ jsxs("div", {
								className: "text-neutral gap-xs flex justify-center",
								onClick: (e) => e.stopPropagation(),
								children: [/* @__PURE__ */ jsx("span", { children: /* @__PURE__ */ jsx(Icon, {
									className: "text-neutral-faded",
									Icon: FaCircle,
									size: "xs"
								}) }), /* @__PURE__ */ jsx("span", { children: /* @__PURE__ */ jsx(DateTime$1, {
									mode: "time",
									timestamp
								}) })]
							}), /* @__PURE__ */ jsxs("div", {
								className: "grid w-full gap-1",
								children: [
									/* @__PURE__ */ jsxs("div", {
										className: "bg-surface-strong flex w-full flex-wrap justify-between truncate rounded p-1 font-semibold",
										children: [/* @__PURE__ */ jsxs("span", {
											className: "gap-xxs flex flex-wrap truncate",
											children: [detail.scope && /* @__PURE__ */ jsx("span", {
												className: "text-color-info",
												children: ACTIVITY_FEED_SCOPE_LABELS[detail.scope]
											}), /* @__PURE__ */ jsx("span", {
												className: "truncate",
												children: title
											})]
										}), locationId && /* @__PURE__ */ jsx(LocationNames, { locationIds: castArray(locationId) })]
									}),
									/* @__PURE__ */ jsx("div", {
										className: "text-neutral font-light",
										children: subTitle
									}),
									/* @__PURE__ */ jsx("div", {
										className: "font-light",
										children: description
									}),
									!!activityTypeTags?.length && /* @__PURE__ */ jsx(Tags, { tags: activityTypeTags }),
									user && /* @__PURE__ */ jsxs("div", {
										className: "flex flex-wrap items-center gap-1",
										children: [
											/* @__PURE__ */ jsx("div", {
												className: "",
												children: /* @__PURE__ */ jsx(UserName, {
													userId: user.id,
													avatar: !userId,
													avatarSize: "xxs"
												})
											}),
											user.status && /* @__PURE__ */ jsx("span", {
												className: "text-neutral font-extralight",
												children: /* @__PURE__ */ jsx("span", {
													className: "rounded px-1",
													style: {
														background: user.status.color,
														color: getColorContrast(user.status.color)
													},
													children: user.status.title
												})
											}),
											user.lastGeoLocation && /* @__PURE__ */ jsxs("div", { children: [/* @__PURE__ */ jsx(Icon, {
												Icon: MdGpsFixed,
												size: "xs"
											}), /* @__PURE__ */ jsxs("span", {
												className: "text-neutral font-extralight",
												children: [
													user.lastGeoLocation.device.type,
													" ",
													user.lastGeoLocation.device.os
												]
											})] }),
											/* @__PURE__ */ jsx(UserAnPCheck, {
												check: ({ anp }) => anp.role.ops.access.viewUsersGeoLocationHistory || user.id === currentUser.id,
												children: user.lastGeoLocation?.relativeLocation && /* @__PURE__ */ jsx(UserGeoRelativeLocationLabel, { relativeLocation: user.lastGeoLocation.relativeLocation })
											})
										]
									})
								]
							})]
						}, getActivityKey(activity));
					})
				})] }, date))
			})
		})]
	});
}
/** Read the initial filter values from the URL on first mount. */
function readInitialFilters() {
	const raw = new URLSearchParams(window?.location?.search || "").get("actfeedfilters");
	if (!raw) return DEFAULT_FILTERS;
	try {
		return {
			...DEFAULT_FILTERS,
			...JSON.parse(raw)
		};
	} catch {
		return DEFAULT_FILTERS;
	}
}
//#endregion
export { ActivityFeed };

//# sourceMappingURL=ActivityFeed.js.map